/**
 * Task 280J — Strategy-Aware Scheduling, Phase A.
 *
 * Pure (no React, no network) deterministic slot planner for the AI content
 * calendar draft. Timing comes from the package-derived entry count + the
 * client's `client_operating_setup`, NOT from the AI: this module computes the
 * date/time for each draft entry, and `applyScheduleSlots` overrides the AI's
 * `scheduled_date` / `scheduled_time_local` in the draft (the user still edits
 * every slot before saving; the save payload shape is unchanged).
 *
 * Calendar math is done in UTC (`Date.UTC`) so it never depends on the current
 * time and is fully unit-testable. The produced dates/times are LOCAL values —
 * matching how the draft already stores `scheduledDate` / `scheduledTimeLocal`
 * (the save path converts local → UTC). `timezone` is carried for context only.
 */

/** Default local time used when the client has no `preferred_times_local`. */
export const DEFAULT_SLOT_TIME = '10:00';

export interface ScheduleSlot {
  /** Local calendar date, `YYYY-MM-DD`. */
  readonly scheduled_date: string;
  /** Local 24h time, `HH:MM`. */
  readonly scheduled_time_local: string;
}

export interface BuildScheduleSlotsInput {
  /** How many slots to produce (the number of draft entries to place). */
  readonly count: number;
  /** Target calendar month, `YYYY-MM`. */
  readonly targetMonth: string;
  /** IANA timezone (context only; slot dates/times are local). */
  readonly timezone: string;
  /** Agency-set earliest intended publish date, `YYYY-MM-DD` or null. */
  readonly firstPublishDate: string | null;
  /** Production lead days added to `firstPublishDate` (>= 0). */
  readonly productionLeadDays: number;
  /** Approval buffer days added to `firstPublishDate` (>= 0). */
  readonly approvalBufferDays: number;
  /** Preferred ISO weekdays (1=Mon..7=Sun); null/empty → every day in window. */
  readonly preferredDays: ReadonlyArray<number> | null;
  /** Preferred local times `HH:MM`; null/empty → `[DEFAULT_SLOT_TIME]`. */
  readonly preferredTimesLocal: ReadonlyArray<string> | null;
}

const TIME_HH_MM = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const DATE_YYYY_MM_DD = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_YYYY_MM = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** UTC ms for a Y/M/D (M is 1-based). */
function utcDay(year: number, month1: number, day: number): number {
  return Date.UTC(year, month1 - 1, day);
}

/** ISO weekday 1=Mon..7=Sun from a UTC timestamp. */
function isoWeekday(ms: number): number {
  const dow = new Date(ms).getUTCDay(); // 0=Sun..6=Sat
  return dow === 0 ? 7 : dow;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatUtcDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** Parse `YYYY-MM-DD` to a UTC ms, or null when malformed / not a real date. */
function parseDate(value: string | null): number | null {
  if (!value) return null;
  const m = DATE_YYYY_MM_DD.exec(value);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const ms = utcDay(y, mo, d);
  const back = new Date(ms);
  // Reject normalization drift (e.g. 2026-02-31 → Mar 03).
  if (back.getUTCFullYear() !== y || back.getUTCMonth() + 1 !== mo || back.getUTCDate() !== d) {
    return null;
  }
  return ms;
}

function clampNonNegInt(n: number): number {
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Build `count` schedule slots distributed across the client's preferred days
 * and times within the target month, never earlier than
 * `firstPublishDate + productionLeadDays + approvalBufferDays`.
 *
 * Distribution is day-major: the first pass places one entry per candidate date
 * at the first time (dates ascending); overflow wraps the dates and advances to
 * the next preferred time. It never drops an entry and never schedules outside
 * the target month.
 */
export function buildScheduleSlots(input: BuildScheduleSlotsInput): ScheduleSlot[] {
  const count = clampNonNegInt(input.count);
  if (count === 0) return [];

  const monthMatch = MONTH_YYYY_MM.exec(input.targetMonth);
  if (!monthMatch) return [];
  const year = Number(monthMatch[1]);
  const month1 = Number(monthMatch[2]);

  const monthStart = utcDay(year, month1, 1);
  // Day 0 of the next month === last day of this month.
  const monthEnd = utcDay(year, month1 + 1, 0);

  // Earliest publishable date = first_publish_date + lead + buffer, clamped into
  // the target month. With no first_publish_date there is no floor → month start.
  const firstPublish = parseDate(input.firstPublishDate);
  let earliest = monthStart;
  if (firstPublish !== null) {
    const offsetDays =
      clampNonNegInt(input.productionLeadDays) + clampNonNegInt(input.approvalBufferDays);
    earliest = firstPublish + offsetDays * 86_400_000;
  }
  if (earliest < monthStart) earliest = monthStart;
  // If the whole month precedes the earliest producible date, collapse to the
  // last day of the month (documented fallback) so we still place entries inside
  // the requested month rather than outside it.
  if (earliest > monthEnd) earliest = monthEnd;

  const preferredDaySet = new Set(
    (input.preferredDays ?? []).filter((d) => Number.isInteger(d) && d >= 1 && d <= 7),
  );

  const allDatesInWindow: number[] = [];
  for (let ms = earliest; ms <= monthEnd; ms += 86_400_000) {
    allDatesInWindow.push(ms);
  }
  // Window is always non-empty (earliest is clamped to <= monthEnd), but guard.
  if (allDatesInWindow.length === 0) allDatesInWindow.push(monthEnd);

  let days: number[];
  if (preferredDaySet.size > 0) {
    days = allDatesInWindow.filter((ms) => preferredDaySet.has(isoWeekday(ms)));
    // No preferred weekday falls in the window → fall back to every day.
    if (days.length === 0) days = allDatesInWindow;
  } else {
    days = allDatesInWindow;
  }

  const times = (input.preferredTimesLocal ?? []).filter((t) => TIME_HH_MM.test(t));
  if (times.length === 0) times.push(DEFAULT_SLOT_TIME);

  const slots: ScheduleSlot[] = [];
  for (let i = 0; i < count; i += 1) {
    const date = days[i % days.length];
    const time = times[Math.floor(i / days.length) % times.length];
    slots.push({ scheduled_date: formatUtcDate(date), scheduled_time_local: time });
  }
  return slots;
}

/**
 * Override each item's local date/time from the planner slots, in order. Items
 * without a corresponding slot are returned unchanged. Generic over the draft
 * item shape so this helper never depends on the page's internal types.
 */
export function applyScheduleSlots<
  T extends { readonly scheduledDate: string; readonly scheduledTimeLocal: string },
>(items: ReadonlyArray<T>, slots: ReadonlyArray<ScheduleSlot>): T[] {
  return items.map((item, i) => {
    const slot = slots[i];
    if (!slot) return item;
    return {
      ...item,
      scheduledDate: slot.scheduled_date,
      scheduledTimeLocal: slot.scheduled_time_local,
    };
  });
}
